import { useMemo, useState } from "react"
import { isNetworkError } from "../network/client"
import { OTHER_OPTION_ID, submitFlagIssue } from "../network/flag"
import { FLAG_SCOPE, flagReasonsFor } from "../models/snapshot"
import { useMenuData } from "../offline/menuData"
import useCloseRequest from "../hooks/useCloseRequest"
import "./FlagIssue.css"

const QUEUED_MESSAGE = "Saved — it will be sent when you're back online."

// Mirrors FlagQuestionsView: predefined reasons as radios, a free-text box for
// "Other", then a THANK YOU / ALERT card over the sheet. Reasons come from the saved menu.
export default function FlagIssue({ itemId, scope = FLAG_SCOPE.MENU_ITEM, onClose }) {
  const { menu, queueFlag } = useMenuData()
  const [selectedId, setSelectedId] = useState("")
  const [note, setNote] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [alert, setAlert] = useState(null)

  const options = useMemo(
    () => [
      ...flagReasonsFor(menu, scope).map((entry) => ({ id: String(entry.id), text: entry.text ?? "" })),
      { id: OTHER_OPTION_ID, text: "Other issue not listed above." }
    ],
    [menu, scope]
  )

  const isOther = selectedId === OTHER_OPTION_ID

  const submit = async () => {
    if (isOther && !note.trim()) {
      setAlert({
        ok: false,
        message: "Please provide a description for 'Other issue not listed above.'"
      })
      return
    }

    const flag = { menuItemId: itemId, commentId: selectedId, customComment: note.trim() }
    setIsSubmitting(true)
    try {
      // Offline the flag waits in the outbox rather than failing.
      if (navigator.onLine === false) {
        await queueFlag(flag)
        setAlert({ ok: true, message: QUEUED_MESSAGE })
        return
      }
      const data = await submitFlagIssue(flag)
      setAlert({
        ok: true,
        message: data?.message ?? "Thanks for flagging this. Our team will take a look."
      })
    } catch (error) {
      if (isNetworkError(error)) {
        await queueFlag(flag)
        setAlert({ ok: true, message: QUEUED_MESSAGE })
      } else {
        setAlert({ ok: false, message: error.message })
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const dismissAlert = () => {
    const wasSuccess = alert?.ok
    setAlert(null)
    if (wasSuccess) onClose()
  }

  // Back dismisses the result card first, then the sheet.
  useCloseRequest(true, alert ? dismissAlert : onClose)

  return (
    <div className="flag" role="dialog" aria-modal="true" aria-label="Flag an Issue">
      <button type="button" className="flag__scrim" aria-label="Close" onClick={onClose} />

      <div className="flag__sheet">
        <span className="flag__grabber" aria-hidden="true" />

        <div className="flag__scroll scroll-y">
          <header className="flag__header">
            <h2 className="flag__title t-s16">Flag an Issue</h2>
            <p className="flag__subtitle">
              {scope === FLAG_SCOPE.VIP
                ? "Please select what's wrong with this guest's details"
                : "Please select what's out of date on this menu item"}
            </p>
          </header>

          <ul className="flag__options">
            {options.map((option, index) => (
              <li key={option.id}>
                <button
                  type="button"
                  className="flag__option"
                  role="radio"
                  aria-checked={selectedId === option.id}
                  onClick={() => {
                    setSelectedId(option.id)
                    if (option.id !== OTHER_OPTION_ID) setNote("")
                  }}
                >
                  <span
                    className={`flag__radio${selectedId === option.id ? " flag__radio--on" : ""}`}
                  />
                  <span className="flag__option-text">{option.text}</span>
                </button>
                {index < options.length - 1 && <span className="flag__divider" />}
              </li>
            ))}
          </ul>

          {isOther && (
            <div className="flag__note">
              <label className="flag__note-label t-p14" htmlFor="flag-note">
                Please specify in detail
              </label>
              <textarea
                id="flag-note"
                className="flag__note-input"
                value={note}
                rows={4}
                onChange={(event) => setNote(event.target.value)}
              />
            </div>
          )}

          <div className="flag__actions">
            <button
              type="button"
              className={`flag__submit t-p14${selectedId ? "" : " flag__submit--disabled"}`}
              disabled={!selectedId || isSubmitting}
              onClick={submit}
            >
              {isSubmitting ? "Submitting…" : "Submit"}
            </button>
            <button type="button" className="flag__cancel t-p14" onClick={onClose}>
              Cancel
            </button>
          </div>
        </div>

        {alert && (
          <div className="flag__alert-layer">
            <div className="flag__alert">
              <span className="flag__alert-grabber" aria-hidden="true" />
              <h3 className="flag__alert-title">{alert.ok ? "Thank You" : "Alert"}</h3>
              <span className="flag__alert-rule" />
              <p className="flag__alert-message">{alert.message}</p>
              <button type="button" className="flag__done" onClick={dismissAlert}>
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
